'use client';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import {
  BonanzaPromoStatus,
  checkIsBonanzaPromoLastRound,
  updateBonanzaData,
} from '../helpers';
import { showTooltip } from '@/helpers/tooltip';
import { useNavigation } from '@/hooks/navigation';
import { STORAGE, useCommonStore, useLocalStorageStore } from '@/store';
import { TooltipTypes } from '@/store/tooltipStore';

export const useBonanzaPromo = () => {
  const { navigate } = useNavigation();
  const {
    t,
    i18n: { language },
  } = useTranslation('bonanzaPromo');

  const {
    user,
    cmsConfig,
    userBonanzaGroup,
    userBonanzaGroupKey,
    bonanzaPromoMenuData,
  } = useCommonStore(
    useShallow((state) => ({
      user: state.user,
      cmsConfig: state.cmsConfig,
      userBonanzaGroup: state.userBonanzaGroup,
      userBonanzaGroupKey: state.userBonanzaGroupKey,
      bonanzaPromoMenuData: state.bonanzaPromoMenuData,
    }))
  );

  const isLockedPromo = useMemo(
    () => userBonanzaGroup === BonanzaPromoStatus.PROMO_LOCKED,
    [userBonanzaGroup]
  );

  const isBonusIssued = useMemo(
    () => userBonanzaGroup === BonanzaPromoStatus.LEVEL_4,
    [userBonanzaGroup]
  );

  const isPromoCompleted = useMemo(() => {
    if (!isLockedPromo || !cmsConfig || !cmsConfig.bonanzaPromo?.dateRange)
      return;

    const isLastRound = checkIsBonanzaPromoLastRound(
      cmsConfig.bonanzaPromo.dateRange.end
    );

    return isLastRound;
  }, [cmsConfig, isLockedPromo]);

  const characterByStatus = useMemo(() => {
    return bonanzaPromoMenuData?.menu.charactersByStatus.find(
      ({ promoStatus }) => promoStatus === userBonanzaGroupKey
    );
  }, [bonanzaPromoMenuData, userBonanzaGroupKey]);

  const handleAcknowledgeBonus = useCallback(() => {
    useLocalStorageStore.getState().push(STORAGE.BONUS_BONANZA_CLICK, true);
    updateBonanzaData(language, user);

    if (cmsConfig?.bonanzaPromo?.dateRange?.end) {
      const isLastRound = checkIsBonanzaPromoLastRound(
        cmsConfig.bonanzaPromo.dateRange.end
      );

      if (isLastRound) {
        showTooltip({
          type: TooltipTypes.INFO,
          body: (
            <div>
              <strong>{t('lastRound.title')}</strong>
              <p>{t('lastRound.description')}</p>
            </div>
          ),
        });
      }
    }
  }, [t, language, user, cmsConfig?.bonanzaPromo?.dateRange?.end]);

  const handleNavigateToBonanza = useCallback(() => {
    navigate('/bonanza');
  }, [navigate]);

  return {
    isLockedPromo,
    isBonusIssued,
    isPromoCompleted,
    characterByStatus,
    handleAcknowledgeBonus,
    handleNavigateToBonanza,
  };
};
